import { formatChangeDetailsTime } from "../DrofusEndringer/drofusChangeDetailsSelectors";
import { valuePreview } from "../DrofusEndringer/drofusChangePresentation";
import type {
  PostMultiplacerDrofusInterfaceProjectChangeEventsApiResponse as IProjectChangeEventsResponse,
  PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiResponse as IProjectRoomGroupsResponse,
} from "../../openapi";
import { createSelector } from "@reduxjs/toolkit";
import type { DrofusForekomstDetailsRef } from "../DrofusForekomster/DrofusForekomstDetailsModal";
import { DROFUS_CHANGE_TYPE_OPTIONS } from "../components/DrofusChangeTypeFilter";

type IDrofusChangeEvent = IProjectChangeEventsResponse["items"][number];

export interface IRoomDrofusChangeRow {
  id: string;
  changeType: IDrofusChangeEvent["changeType"];
  disciplines: string[];
  title: string;
  fieldLabel: string;
  oldValue: string;
  newValue: string;
  runStartedAt: string;
  detectedAtLabel: string;
  color: string;
  occurrenceRef: DrofusForekomstDetailsRef | null;
}

export interface IRoomDrofusChangeSummary {
  added: number;
  removed: number;
  updated: number;
  total: number;
}

const getChangeTypeColor = (changeType: IDrofusChangeEvent["changeType"]): string =>
  DROFUS_CHANGE_TYPE_OPTIONS.find((option) => option.value === changeType)?.activeBackground ??
  "#868e96";

const getOccurrenceRef = (event: IDrofusChangeEvent): DrofusForekomstDetailsRef | null => {
  if (event.entityType !== "occurrence") return null;

  const occurrenceId =
    typeof event.entityId === "number"
      ? event.entityId
      : Number.parseInt(String(event.entityId).replace(/^occurrence:/, ""), 10);
  if (!Number.isFinite(occurrenceId)) return null;

  return {
    changeEvent: event,
    projectNo: event.projectNo,
    occurrenceId,
    runId: event.changeType === "removed" ? event.baseRunId : event.runId,
    ...(event.changeType === "removed"
      ? {
          removedChange: {
            context: event.context,
            runStartedAt: event.runStartedAt,
            affectedDisciplines: event.affectedDisciplines,
            drofusLog: event.drofusLog,
          },
        }
      : {}),
  };
};

export function formatRoomDrofusChangeRows(events: IDrofusChangeEvent[]): IRoomDrofusChangeRow[] {
  return events
    .map((event) => {
      const articleName = event.context.article?.name?.trim();
      const compCode = event.context.article?.compCode?.trim();
      const fallbackTitle = event.entityType === "room" ? "Rom" : "Forekomst";
      const titleParts = [compCode, articleName].filter(Boolean);

      return {
        id: `${event.runId}:${event.stableKey}:${event.changeKey}`,
        changeType: event.changeType,
        disciplines: event.affectedDisciplines,
        title: titleParts.length > 0 ? titleParts.join(" - ") : fallbackTitle,
        fieldLabel: event.displayName ?? event.normalizedPath ?? event.changeKey,
        oldValue: valuePreview(event, "oldValue"),
        newValue: valuePreview(event, "newValue"),
        runStartedAt: event.runStartedAt,
        detectedAtLabel: formatChangeDetailsTime(event.runStartedAt),
        color: getChangeTypeColor(event.changeType),
        occurrenceRef: getOccurrenceRef(event),
      };
    })
    .sort((a, b) => b.runStartedAt.localeCompare(a.runStartedAt));
}

export const selectTableRoomDrofusChangeRows = createSelector(
  (result: { currentData?: IProjectChangeEventsResponse }) => result.currentData?.items,
  (items): IRoomDrofusChangeRow[] => formatRoomDrofusChangeRows(items ?? [])
);

export const selectTableRoomDrofusChangeSummary = createSelector(
  (result: { currentData?: IProjectRoomGroupsResponse }) => result.currentData?.items[0],
  (group): IRoomDrofusChangeSummary => ({
    added: group?.addedCount ?? 0,
    removed: group?.removedCount ?? 0,
    updated: group?.updatedCount ?? 0,
    total: group?.changeCount ?? 0,
  })
);
